// Who may sign in to an app: directory users and groups, saved together
// (POST /api/v1/idp/apps/saveAssignments replaces the whole list; max 500).
//
// Users come from POST /ad/GetAllAdUsers (filters.directoryName — its adId
// filter only matches AD directories in "Registered" state), groups from
// POST /ad/GetAllGroupsByDomain (adId). The directories are the tenant's Entra
// directories: users sign in through an Entra sign-in source (AD is not
// available as one yet).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Select, Spin, message } from "antd";
import { useNavigate } from "react-router-dom";
import { Info, Users, XCircle } from "lucide-react";

import { logOutUser } from "../../common";
import { DOMAIN_ID, ORG_ID } from "../../constants";
import { CountChip } from "../../design-system/tableUi";
import { AN_COLORS } from "../../design-system/tokens";
import { listAppAssignments, saveAppAssignments } from "../../service/idpService";
import { dispatchGetAllAdUsers, dispatchGetAllDoamins, dispatchGetAllGroupsByDomain } from "../../utils/apiservice";
import { FieldLabel, Note, PanelHead, card } from "../enduser/ssh-proxy/sshProxyUi";
import { HelpText, openIdentitySources, reportIdpError } from "./appsUi";

const MAX_ASSIGNMENTS = 500;

const userLabel = (u) => u.user_principal_name || u.emailId || u.username || String(u.id);

// The directory calls take callbacks; failures share one handler.
const directoryFailure = (msg) => ({
  onFailure: ({ message: m, isLogout } = {}) => {
    if (isLogout) return logOutUser();
    return msg.error(m || "Could not read the directory.");
  },
  onError: ({ message: m } = {}) => msg.error(m || "Could not read the directory."),
});

export default function AppAssignments({ app, onNotFound }) {
  const navigate = useNavigate();
  const [msg, msgCtx] = message.useMessage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");
  // What is saved, to tell whether anything changed.
  const [saved, setSaved] = useState({ users: [], groups: [] });
  // principal → label, for every user seen (assignments and search results).
  const [labels, setLabels] = useState({});
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);

  const [dirs, setDirs] = useState(null);
  const [dirId, setDirId] = useState(undefined);
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dirGroups, setDirGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const searchTimer = useRef(null);

  const apply = useCallback((list) => {
    const us = list.filter((a) => a.type === "user");
    const gs = list.filter((a) => a.type === "group").map((a) => a.principal);
    setLabels((prev) => ({ ...prev, ...Object.fromEntries(us.map((a) => [a.principal, a.label || `User ${a.principal}`])) }));
    setUsers(us.map((a) => a.principal));
    setGroups(gs);
    setSaved({ users: us.map((a) => a.principal), groups: gs });
  }, []);

  // The app's assignments.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    listAppAssignments(app.id)
      .then((res) => alive && apply(res?.assignments || []))
      .catch((err) => alive && reportIdpError(err, { msg, fallback: "Could not read the assignments.", onNotFound }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [app.id, apply, msg, onNotFound]);

  // The tenant's Entra directories.
  useEffect(() => {
    dispatchGetAllDoamins({
      payload: {
        tenantId: DOMAIN_ID(),
        orgId: ORG_ID(),
        pageNo: 1,
        pageSize: 100,
        filters: { identitySource: "EntraID", status: "Active" },
      },
      onSuccess: (res) => {
        const list = (res?.domains || []).filter((d) => d?.status !== "Deleted");
        setDirs(list);
        if (list.length) setDirId((cur) => cur ?? list[0].id);
      },
      ...directoryFailure(msg),
    });
  }, [msg]);

  const dir = useMemo(() => (dirs || []).find((d) => d.id === dirId), [dirs, dirId]);

  const searchUsers = useCallback(
    (q) => {
      if (!dir) return;
      setSearching(true);
      dispatchGetAllAdUsers({
        payload: {
          tenantId: DOMAIN_ID(),
          orgId: ORG_ID(),
          pageNo: 1,
          pageSize: 50,
          filters: { emailId: q || "", directoryName: dir.directoryName || "" },
        },
        onSuccess: (res) => {
          setSearching(false);
          const list = (res?.users || []).map((u) => ({ principal: String(u.id), label: userLabel(u) }));
          setLabels((prev) => ({ ...prev, ...Object.fromEntries(list.map((u) => [u.principal, u.label])) }));
          setUserResults(list);
        },
        onFailure: (arg) => {
          setSearching(false);
          directoryFailure(msg).onFailure(arg);
        },
        onError: (arg) => {
          setSearching(false);
          directoryFailure(msg).onError(arg);
        },
      });
    },
    [dir, msg],
  );

  // A directory picked: its first users, and all its groups.
  useEffect(() => {
    if (!dir) return;
    searchUsers("");
    setGroupsLoading(true);
    dispatchGetAllGroupsByDomain({
      payload: { tenantId: DOMAIN_ID(), orgId: ORG_ID(), adId: dir.id, pageNo: 1, pageSize: 500, filter: "" },
      onSuccess: (res) => {
        setGroupsLoading(false);
        setDirGroups((res?.groups || []).map((g) => g.groupName || g.name).filter(Boolean));
      },
      onFailure: (arg) => {
        setGroupsLoading(false);
        directoryFailure(msg).onFailure(arg);
      },
      onError: (arg) => {
        setGroupsLoading(false);
        directoryFailure(msg).onError(arg);
      },
    });
  }, [dir, searchUsers, msg]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);
  const onUserSearch = (q) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchUsers(q.trim()), 350);
  };

  const userOptions = useMemo(() => {
    const seen = new Set();
    return [...users.map((p) => ({ principal: p, label: labels[p] || `User ${p}` })), ...userResults]
      .filter((u) => (seen.has(u.principal) ? false : seen.add(u.principal)))
      .map((u) => ({ value: u.principal, label: u.label }));
  }, [users, userResults, labels]);

  const groupOptions = useMemo(
    () => [...new Set([...groups, ...dirGroups])].map((g) => ({ value: g, label: g })),
    [groups, dirGroups],
  );

  const total = users.length + groups.length;
  const same = (a, b) => a.length === b.length && [...a].sort().join("\n") === [...b].sort().join("\n");
  const dirty = !same(users, saved.users) || !same(groups, saved.groups);

  const save = () => {
    if (total > MAX_ASSIGNMENTS) {
      setServerError(`At most ${MAX_ASSIGNMENTS} users and groups.`);
      return;
    }
    setSaving(true);
    setServerError("");
    saveAppAssignments(app.id, [
      ...users.map((principal) => ({ type: "user", principal })),
      ...groups.map((principal) => ({ type: "group", principal })),
    ])
      .then((res) => {
        apply(res?.assignments || []);
        msg.success("Assignments saved.");
      })
      .catch((err) =>
        reportIdpError(err, { msg, fallback: "Could not save the assignments.", setInline: setServerError, onNotFound }),
      )
      .finally(() => setSaving(false));
  };

  const noDirs = Array.isArray(dirs) && dirs.length === 0;

  return (
    <div style={{ ...card, padding: 18, maxWidth: "56rem" }}>
      {msgCtx}
      <PanelHead
        title="Assignments"
        desc="Only assigned users and members of assigned groups can sign in to this app. Others see 'No access'."
        right={<CountChip count={total} label="assigned" />}
      />

      {loading ? (
        <div style={{ padding: 30, textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {noDirs && (
            <Note tone="warn" icon={<Info size={15} />}>
              No Entra directory is connected yet, so there are no users or groups to pick.{" "}
              <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={() => openIdentitySources(navigate)}>
                Connect one in Identity → Identity Sources
              </Button>
            </Note>
          )}

          {dirs && dirs.length > 1 && (
            <div>
              <FieldLabel>Directory</FieldLabel>
              <Select
                value={dirId}
                onChange={(v) => {
                  setDirId(v);
                  setUserResults([]);
                  setDirGroups([]);
                }}
                options={dirs.map((d) => ({ value: d.id, label: d.directoryName || d.domainName || `Directory ${d.id}` }))}
                style={{ width: "100%", maxWidth: "24rem" }}
              />
              <HelpText>Users and groups are listed from this directory. Assignments from other directories are kept.</HelpText>
            </div>
          )}

          <div>
            <FieldLabel>Users</FieldLabel>
            <Select
              mode="multiple"
              value={users}
              onChange={setUsers}
              options={userOptions}
              showSearch
              filterOption={false}
              onSearch={onUserSearch}
              onDropdownVisibleChange={(open) => open && !userResults.length && searchUsers("")}
              notFoundContent={searching ? <Spin size="small" /> : dir ? "No users found" : "No directory"}
              placeholder={dir ? "Search users by email" : "No directory to pick from"}
              disabled={!dir && users.length === 0}
              suffixIcon={<Users size={14} />}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <FieldLabel>Groups</FieldLabel>
            <Select
              mode="multiple"
              value={groups}
              onChange={setGroups}
              options={groupOptions}
              showSearch
              optionFilterProp="label"
              loading={groupsLoading}
              notFoundContent={groupsLoading ? <Spin size="small" /> : "No groups"}
              placeholder={dir ? "Pick groups" : "No directory to pick from"}
              disabled={!dir && groups.length === 0}
              style={{ width: "100%" }}
            />
            <HelpText>Members of a group, directly or through nested groups, can sign in.</HelpText>
          </div>

          {serverError && (
            <Note tone="danger" icon={<XCircle size={15} />}>
              {serverError}
            </Note>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "flex-end" }}>
            <span style={{ fontSize: 12, color: total > MAX_ASSIGNMENTS ? AN_COLORS.danger : AN_COLORS.muted }}>
              {total} of {MAX_ASSIGNMENTS}
            </span>
            <Button disabled={!dirty || saving} onClick={() => apply([...saved.users.map((p) => ({ type: "user", principal: p, label: labels[p] })), ...saved.groups.map((g) => ({ type: "group", principal: g }))])}>
              Discard
            </Button>
            <Button type="primary" loading={saving} disabled={!dirty} onClick={save}>
              Save assignments
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
